import { useEffect, useMemo, useRef } from 'react';

import { GENERATED_ACTIVITY_DEFAULTS_VERSION } from '@revelio/data-access/activity-defaults';

import { kibanaLogger } from '../../../../shared/logging/kibana-logger';
import { useActivityTaxonomyTree } from '../../../../shared/selection-lists/hooks/useActivityTaxonomyTree';
import { ACTIVITY_TAXONOMY_SELECTION_LIST_KEYS } from '../../../../shared/selection-lists/types';
import {
  PreferenceScope,
  UserPreferenceKey,
} from '../../../../shared/user-preferences/types';
import { deriveEffectiveTaxonomiesForScope } from '../../../../shared/user-preferences/utils/derive-effective-taxonomies';
import { PreferenceBrowserStorage } from '../../../../shared/user-preferences/utils/preference-session-storage';
import { reconcileVersionedDefaults } from '../../../../shared/user-preferences/utils/reconcileVersionedDefaults';
import { useToast } from '../../hooks/useToast';
import type { TreeFilter } from '../types/filters';

/** How many activities to seed when falling back to the live tree. */
const DEFAULT_ACTIVITY_COUNT = 5;

/**
 * Surfaces the activity default-filter version reconciliation as a side effect
 * and, when both the saved preference and the baked-in defaults are stale,
 * supplies the first N top-level activities from the live taxonomy tree as a
 * fallback selection.
 *
 * - saved stale (baked current) → info toast ("no longer apply"), caller uses coded defaults.
 * - saved + baked stale → error toast + Kibana log, caller uses `firstFromLiveSelections`.
 *
 * Toasts/logs fire from an effect (never during the synchronous default-map build).
 */
export const useActivityDefaultsStaleNotice = ({
  enabled,
  currentActivityVersion,
}: {
  enabled: boolean;
  currentActivityVersion?: string;
}): { firstFromLiveSelections?: TreeFilter['selections'] } => {
  const toast = useToast();
  const notifiedRef = useRef<string | null>(null);

  const activityPrefs = PreferenceBrowserStorage.get(PreferenceScope.ACTIVITY);
  const savedSelections = activityPrefs?.[UserPreferenceKey.DefaultFilters];
  // Taxonomy choice is GLOBAL (derived: adaptive → generic on activity pages).
  const globalTaxonomies = deriveEffectiveTaxonomiesForScope(
    PreferenceBrowserStorage.get(PreferenceScope.GLOBAL)?.[
      UserPreferenceKey.Taxonomies
    ],
    PreferenceScope.ACTIVITY
  );
  const savedVersion = globalTaxonomies?.activityTaxonomyVersion;

  const result = reconcileVersionedDefaults({
    hasSaved: !!savedSelections && savedSelections.length > 0,
    savedVersion,
    currentVersion: currentActivityVersion,
    bakedVersion: GENERATED_ACTIVITY_DEFAULTS_VERSION,
  });

  // Only read the live tree when we actually need the fallback. The tree hook
  // has no pause; when not needed the derived selections are simply ignored.
  const needLive = enabled && result.generatedStale;
  const activityTaxonomyId =
    typeof globalTaxonomies?.activity === 'number'
      ? globalTaxonomies.activity
      : 0;
  const { activityTree } = useActivityTaxonomyTree({
    overrideTaxonomyId: activityTaxonomyId,
    taxonomyVersionOverride: currentActivityVersion,
  });

  const firstFromLiveSelections = useMemo<
    TreeFilter['selections'] | undefined
  >(() => {
    if (!needLive || !activityTree || activityTree.length === 0) {
      return undefined;
    }
    const topLevelId = ACTIVITY_TAXONOMY_SELECTION_LIST_KEYS[0];
    if (!topLevelId) return undefined;
    return activityTree.slice(0, DEFAULT_ACTIVITY_COUNT).map((node) => ({
      id: String(node.id),
      name: node.name,
      levelId: topLevelId,
    }));
  }, [needLive, activityTree]);

  useEffect(() => {
    if (!enabled) return;
    const key = `${result.source}:${currentActivityVersion ?? ''}`;
    if (notifiedRef.current === key) return;

    if (result.generatedStale) {
      notifiedRef.current = key;
      kibanaLogger?.error(
        `Activity default filters stale: generated ${GENERATED_ACTIVITY_DEFAULTS_VERSION} != live ${currentActivityVersion}`
      );
      const id = 'activity-defaults-generated-stale';
      if (!toast.isActive(id)) {
        toast({
          id,
          title: 'Default activities could not be loaded',
          description:
            'A temporary activity selection is being shown. Please review and re-select your activities.',
          status: 'error',
          duration: 8000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } else if (result.savedStale) {
      notifiedRef.current = key;
      const id = 'activity-defaults-out-of-date';
      if (!toast.isActive(id)) {
        toast({
          id,
          title: 'Your default activities no longer apply',
          description:
            'They were chosen for a different activity taxonomy, so the standard defaults are showing. Set new defaults in Preferences.',
          status: 'info',
          duration: 6000,
          isClosable: true,
          position: 'top-right',
        });
      }
    }
  }, [
    enabled,
    result.source,
    result.savedStale,
    result.generatedStale,
    currentActivityVersion,
    toast,
  ]);

  return { firstFromLiveSelections };
};
